import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '../auth/use-auth'
import { Brand } from '../components/brand'
import { Button } from '../components/button'
import { sanitiseReturnTo } from '../auth/use-return-to'

/**
 * /sign-in landing page.
 *
 * Per the MUR "Mockups" page wireframe and MUR "7. Application Architecture" UX-6 verdict (splash, not
 * auto-redirect): a near-blank splash with the wordmark, a one-line
 * subhead, and a single primary CTA. Auto-focuses the CTA on mount so
 * keyboard users land on the action immediately. Copy is verbatim from
 * the wireframe.
 *
 * `returnTo` is read from React Router location state (set by RouteGuard
 * when the user lands here via redirect). It is round-tripped through
 * Entra via the OIDC `state` parameter so it survives the IdP redirect.
 */
export function SignInPage(): JSX.Element {
  const { signIn, signedIn, inProgress } = useAuth()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const stateReturnTo = (location.state as { returnTo?: string } | null)?.returnTo
  const returnTo = stateReturnTo ? (sanitiseReturnTo(stateReturnTo) ?? undefined) : undefined

  // Focus once the button is reachable. We re-run when `inProgress` flips
  // from true (MSAL still resolving) to false so keyboard users land on
  // the CTA as soon as it is interactive.
  useEffect(() => {
    if (!inProgress) {
      buttonRef.current?.focus()
    }
  }, [inProgress])

  // If the user is already authenticated when this page mounts (or becomes
  // so while still on this page - which is the dev-mode mock flow:
  // click the button -> mock flips signedIn=true synchronously -> no IdP
  // hop -> we navigate to /accounts ourselves), bounce them to the
  // returnTo path or /accounts.
  useEffect(() => {
    if (signedIn && !inProgress) {
      navigate(returnTo ?? '/members', { replace: true })
    }
  }, [signedIn, inProgress, returnTo, navigate])

  const onSignIn = () => {
    void signIn(returnTo)
  }

  return (
    <div className="flex h-full flex-col">
      <main
        id="main"
        role="main"
        className="mx-auto flex w-full max-w-[640px] flex-1 flex-col items-center justify-center gap-murmuration-5 px-murmuration-6 text-center"
      >
        {/* The lockup carries the wordmark, so it is the page heading. There is
            no second brand mark in the header: one on a near-blank splash is
            enough. */}
        <h1 className="m-0">
          <Brand variant="splash" />
        </h1>
        <p className="text-murmuration-body text-text-secondary">Back-office for Suits Me staff</p>
        <Button
          ref={buttonRef}
          variant="primary"
          onClick={onSignIn}
          disabled={inProgress}
          className="h-10 px-murmuration-5"
        >
          Sign in with Microsoft
        </Button>
        <p className="max-w-[420px] text-murmuration-label text-text-muted">
          You will be redirected to Microsoft to authenticate. MFA is required.
        </p>
      </main>
    </div>
  )
}
